import { PayloadAction } from "@reduxjs/toolkit";
import { takeLatest, put, call } from "redux-saga/effects";
import {
  loginSuccess,
  loginFailure,
  signupPasswordSuccess,
  signupSharePointSuccess,
  verifyEmailSuccess,
  verifyEmailFailure,
  checkSharePointConfig,
  checkSharePointConfigSuccess,
  checkSharePointConfigFailure,
} from "../reducers/authSlice";
import { notify } from "../../utils/notification";
import axiosInstance from "../../services/axios";
import { API_ENDPOINTS } from "../../constants/api";
import { AxiosResponse } from "axios";
import { LoginResponse } from "../../types/auth";
import { ROLES } from "constants/roles";

interface LoginPayload {
  email: string;
  password: string;
}

function* loginSaga(
  action: PayloadAction<LoginPayload>,
): Generator<any, void, AxiosResponse<LoginResponse>> {
  try {
    const response = yield call(
      axiosInstance.post,
      API_ENDPOINTS.AUTH.SIGN_IN,
      action.payload,
    );
    yield put(loginSuccess(response.data));
    // After successful login, check SharePoint config only for Archivist role
    if (
      response.data.data.roles.some((role) => role.name === ROLES.ARCHIVIST)
    ) {
      yield put(checkSharePointConfig());
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.errors?.message || error.message || "Login failed";
    yield put(loginFailure(errorMessage));
  }
}

function* signupPasswordSaga(
  action: PayloadAction<{ token: string; password: string }>,
): Generator<any, void, any> {
  try {
    yield call(
      axiosInstance.post,
      API_ENDPOINTS.AUTH.SIGN_UP + "/password",
      action.payload,
    );
    yield put(signupPasswordSuccess());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "Password setup failed";
    yield put(loginFailure(errorMessage));
  }
}

function* signupSharePointSaga(
  action: PayloadAction<{ token: string; sharePointKey: string }>,
): Generator<any, void, AxiosResponse<LoginResponse>> {
  try {
    const response = yield call(
      axiosInstance.post,
      API_ENDPOINTS.AUTH.SIGN_UP + "/complete",
      action.payload,
    );
    yield put(signupSharePointSuccess(response.data));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "SharePoint setup failed";
    yield put(loginFailure(errorMessage));
  }
}

function* verifyEmailSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    yield call(axiosInstance.post, API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
      token: action.payload,
    });
    yield put(verifyEmailSuccess());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "Email verification failed";
    yield put(verifyEmailFailure(errorMessage));
  }
}

function* checkSharePointConfigSaga(): Generator<any, void, any> {
  try {
    const response = yield call(
      axiosInstance.get,
      API_ENDPOINTS.SHAREPOINT.CONFIG,
    );
    const { success, message } = response.data.data;

    if (success) {
      yield put(checkSharePointConfigSuccess({ success: true }));
    } else {
      yield notify.warning({
        message: "SharePoint Configuration Required",
        description: message || "Please configure your SharePoint integration.",
      });
      yield put(checkSharePointConfigSuccess({ success: false }));
    }
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.errors?.message ||
      error.message ||
      "Failed to check SharePoint configuration";
    notify.error({
      message: "SharePoint Configuration Error",
      description: errorMessage,
    });
    yield put(checkSharePointConfigFailure(errorMessage));
  }
}

export default function* authSaga(): Generator {
  yield takeLatest("auth/loginRequest", loginSaga);
  yield takeLatest("auth/signupPasswordRequest", signupPasswordSaga);
  yield takeLatest("auth/signupSharePointRequest", signupSharePointSaga);
  yield takeLatest("auth/verifyEmailRequest", verifyEmailSaga);
  yield takeLatest("auth/checkSharePointConfig", checkSharePointConfigSaga);
}
